import React from 'react';
import { ReportAttachmentContent } from '../interfaces/Frameworks';
import { Theme } from '../Theme';

type ReportAttachmentContentPanelProps = {
    clientId: string;
    documentId: string;
    title: string;
    onGetContent: (clientId: string, documentId: string) => Promise<ReportAttachmentContent>;
    onSaveContent: (clientId: string, documentId: string, sourceVersion: string, text: string) => Promise<void>;
};

// Retain useful application errors without exposing generic callable transport codes.
function contentError(error: unknown, fallback: string): string {
    const details = error as {code?: string; message?: string} | null;
    if (details?.code === 'functions/failed-precondition') {
        return 'This file content changed. Reload its content before saving corrections.';
    }
    const message = details?.message?.trim();
    return message && !['internal', 'unavailable', 'unknown', 'unauthenticated'].includes(message.toLowerCase()) ? message : fallback;
}

// Let therapists compare extracted evidence with the adjacent original and explicitly save corrections.
export function ReportAttachmentContentPanel(props: ReportAttachmentContentPanelProps) {
    const {clientId, documentId, title, onGetContent, onSaveContent} = props;
    const scope = JSON.stringify([clientId, documentId]);
    const [state, setState] = React.useState<{
        scope: string; expanded: boolean; loading: boolean; saving: boolean;
        content: ReportAttachmentContent | null; draft: string; error: string; saved: boolean; stale: boolean;
    }>({scope, expanded: false, loading: false, saving: false, content: null, draft: '', error: '', saved: false, stale: false});
    const requestVersion = React.useRef(0);
    const currentScope = React.useRef(scope);
    currentScope.current = scope;

    // A late response must never populate a different Client's attachment or an unmounted panel.
    React.useEffect(() => {
        setState({scope, expanded: false, loading: false, saving: false, content: null, draft: '', error: '', saved: false, stale: false});
        return () => { requestVersion.current += 1; };
    }, [scope]);

    const visible = state.scope === scope;
    const expanded = visible && state.expanded;
    const dirty = state.content?.status === 'ready' && state.draft !== state.content.text;

    // Read on demand so opening a timeline does not fetch every file's clinical content.
    const load = async () => {
        const version = ++requestVersion.current;
        setState((previous) => ({...previous, scope, expanded: true, loading: true, error: '', saved: false}));
        try {
            const content = await onGetContent(clientId, documentId);
            if (version === requestVersion.current && scope === currentScope.current) {
                setState((previous) => ({...previous, content, draft: content.text, loading: false, stale: false}));
            }
        } catch (error) {
            if (version === requestVersion.current && scope === currentScope.current) {
                setState((previous) => ({...previous, loading: false, error: contentError(error, 'Could not load file content. Please try again.')}));
            }
        }
    };

    // Save only on explicit action, retaining the draft when the server rejects a stale source.
    const save = async () => {
        if (!state.content || state.content.status !== 'ready' || !state.draft.trim() || state.saving) return;
        const version = ++requestVersion.current;
        const content = state.content;
        const draft = state.draft;
        setState((previous) => ({...previous, saving: true, error: '', saved: false}));
        try {
            await onSaveContent(clientId, documentId, content.sourceVersion, draft);
            if (version === requestVersion.current && scope === currentScope.current) {
                setState((previous) => ({...previous, saving: false, saved: true, content: {...content, text: draft, reviewed: true}}));
            }
        } catch (error) {
            if (version === requestVersion.current && scope === currentScope.current) {
                setState((previous) => ({
                    ...previous, saving: false,
                    stale: (error as {code?: string})?.code === 'functions/failed-precondition',
                    error: contentError(error, 'Could not save corrections. Please try again.'),
                }));
            }
        }
    };

    return (
        <div style={styles.panel}>
            <button type='button' style={styles.button} aria-expanded={expanded}
                disabled={visible && state.saving}
                onClick={() => {
                    if (expanded) setState((previous) => ({...previous, expanded: false}));
                    else if (visible && (dirty || state.saved)) setState((previous) => ({...previous, expanded: true}));
                    else void load();
                }}>
                {expanded ? 'Hide file content' : 'Review file content'}
            </button>
            {expanded && (
                <div style={styles.content}>
                    {state.loading && <div role='status'>Loading file content...</div>}
                    {!!state.error && <div role='alert' style={styles.error}>{state.error}</div>}
                    {!state.loading && state.content?.status === 'not_processed' && (
                        <p style={styles.note}>File contents become available after processing for a report. If a report is being generated, reload when it completes.</p>
                    )}
                    {!state.loading && state.content?.status === 'ready' && (
                        <>
                            <p style={styles.note}>Compare this text with the original file and correct any misread details. Saving confirms you have checked unclear passages. Saved corrections replace the extracted content in future reports.</p>
                            {state.content.warnings.length > 0 && (
                                <div style={styles.warnings}>
                                    <strong>{state.saved ? 'Original file reading notes' : 'File content needs review'}</strong>
                                    <ul>{state.content.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
                                </div>
                            )}
                            <textarea aria-label={`File content for ${title}`} value={state.draft}
                                disabled={state.saving} style={styles.editor}
                                onChange={(event) => setState((previous) => ({...previous, draft: event.target.value, saved: false}))} />
                            <div style={styles.actions}>
                                <button type='button' style={styles.button} onClick={() => void save()}
                                    disabled={state.saving || state.stale || !state.draft.trim() || (!dirty && !!state.content.reviewed)}>
                                    {state.saving ? 'Saving corrections...' : 'Save corrections'}
                                </button>
                                {dirty && !state.stale && <button type='button' style={styles.button} disabled={state.saving}
                                    onClick={() => setState((previous) => ({...previous, draft: previous.content?.text || '', error: ''}))}>Discard changes</button>}
                            </div>
                            {state.saved ? <div role='status'>Corrections saved. Future reports will use this content.</div> :
                                state.content.reviewed && <div style={styles.note}>Reviewed content</div>}
                        </>
                    )}
                    {!state.loading && <button type='button' style={styles.button} onClick={() => void load()}
                        disabled={state.saving || (!!dirty && !state.stale)}>Reload file content</button>}
                </div>
            )}
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    panel: {marginTop: 12, fontSize: 14},
    content: {display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 10, marginTop: 12},
    button: {border: Theme.Colours.border, borderRadius: 6, background: Theme.Colours.workArea, color: Theme.Colours.text, padding: '7px 12px', cursor: 'pointer'},
    note: {margin: 0, color: '#5c6370', lineHeight: 1.5},
    editor: {width: '100%', minHeight: 260, maxHeight: 640, resize: 'vertical', boxSizing: 'border-box', border: Theme.Colours.border, borderRadius: 6, padding: 12, font: 'inherit', lineHeight: 1.5},
    actions: {display: 'flex', gap: 8, flexWrap: 'wrap'},
    warnings: {padding: 12, borderRadius: 6, background: '#fff7db', color: '#6b5100', width: '100%', boxSizing: 'border-box'},
    error: {color: '#b42318'},
};
